import type { EventId, TemplateView } from '@ps/isp-web-services-types';
import { isEmpty, negate } from 'lodash';

import { withEventBasedMarketTemplateModelSelectors } from '@swf/sports/models/market-templates/eventBased';
import { isEventTemplateView } from '@swf/sports/types/templates';

import { defaultState } from './defaultState';
import type { CompetitionEventsTemplatesView } from './types';

const filterGroups = ({
  events,
  markets,
  templateGroups,
  selectEventsForGroup = 'SINGLES'
}: {
  events: CompetitionEventsTemplatesView['events'];
  markets: CompetitionEventsTemplatesView['markets'];
  templateGroups: CompetitionEventsTemplatesView['templateGroups'];
  selectEventsForGroup?: 'SINGLES' | 'DOUBLES';
}): TemplateView[][][] =>
  templateGroups
    .map((templateGroup) =>
      templateGroup
        .map((template) =>
          template.filter((tpl) => {
            let eventId: EventId | undefined;

            if (isEventTemplateView(tpl)) {
              ({ eventId } = tpl.template);
            } else {
              const marketTemplate = tpl.template;
              const marketId =
                'marketId' in marketTemplate
                  ? marketTemplate.marketId
                  : 'marketIds' in marketTemplate
                  ? marketTemplate.marketIds[0]
                  : null;

              if (!marketId) return false;

              eventId = markets[marketId]?.eventId;
            }

            if (!eventId) return false;

            const { eventName, enrichedParticipants } = events[eventId];

            const isDoubles = enrichedParticipants
              ? enrichedParticipants.every(({ participant }) => participant.includes('/'))
              : eventName.includes('/');

            return selectEventsForGroup === 'SINGLES' ? !isDoubles : isDoubles;
          })
        )
        .filter(negate(isEmpty))
    )
    .filter(negate(isEmpty));

export const selectors = withEventBasedMarketTemplateModelSelectors(defaultState, (slice) => ({
  hasPreloaded: () => slice((state) => state.hasPreloaded),
  isLoading: () => slice((state) => state.isLoading),
  showOutrights: () => slice((state) => state.showOutrights),
  templateGroups: () => slice((state) => state.templateGroups),
  templateGroupsForSingles: () =>
    slice(({ templateGroups, events, markets }) =>
      filterGroups({ templateGroups, events, markets, selectEventsForGroup: 'SINGLES' })
    ),
  templateGroupsForDoubles: () =>
    slice(({ templateGroups, events, markets }) =>
      filterGroups({ templateGroups, events, markets, selectEventsForGroup: 'DOUBLES' })
    ),
  errorCode: () => slice((state) => state.errorCode),
  hasInPlayMarkets: () => slice((state) => state.hasInPlayMarkets)
}));
